import * as fileApi from '@/api/file';
import { Database as db } from '@/utils/persistence';
import { UploadFile } from 'antd';
import { getAntdApp } from '@/utils/antdApp';
import pathbe from 'path-browserify-esm';
import { Subject } from 'rxjs';
import { getUnsupportedCharsDescription, validateLegalString } from './illegalStringValidator';
import { fileEventQueue } from './fileEvent';
import { websocketService, WSMessageType } from './websocketService';
import i18n from '../../i18n';


export const getLanguageByFileName = (fileName: string): string => {
    const extension = fileName.split('.').pop()?.toLowerCase();
    switch (extension) {
        case 'ts':
        case 'tsx':
            return 'typeScript';
        case 'js':
        case 'jsx':
            return 'javaScript';
        case 'html':
            return 'html';
        case 'css':
            return 'css';
        case 'json':
            return 'json';
        case 'md':
            return 'markdown';
        case 'py':
            return 'python';
        case 'java':
            return 'java';
        case 'rb':
            return 'ruby';
        case 'php':
            return 'php';
        case 'cpp':
        case 'cxx':
        case 'cc':
        case 'c':
            return 'c++';
        case 'cs':
            return 'c#';
        case 'go':
            return 'go';
        case 'rs':
            return 'rust';
        case 'swift':
            return 'swift';
        case 'kt':
        case 'kts':
            return 'kotlin';
        default:
            return 'plaintext';
    }
};

export type FileEvent = {
    type: 'create' | 'update' | 'delete' | 'rename' | 'refresh' | 'move' | 'init';
    path: string;
    isDir?: boolean;
    newPath?: string;
    content?: string;
};



class FileService {
    private fileEvents = new Subject<FileEvent>();
    public baseHome: string | undefined = undefined;

    public pathListened: string[] = [];
    public jobPathListened: string[] = []; // 新增：专门监听作业路径的属性
    private writeDebounceTimers: Record<string, NodeJS.Timeout> = {};
    public isConnecting = false; // 标志 WebSocket 通信是否正常
    private degradedRefreshTimer: NodeJS.Timeout | null = null;
    private needsResync = false;


    constructor() {
        console.log("file service constructor start")
        // 注册文件变化消息处理器
        websocketService.on(WSMessageType.FS_WATCH_PATHS, this.handleFileChange.bind(this));
        websocketService.on(WSMessageType.FS_WATCH_EVENT, this.handleFsWatchEvent.bind(this));
        websocketService.on(WSMessageType.CONNECTED, () => {
            this.isConnecting = true;
            this.stopDegradedRefresh();
        });
        websocketService.on(WSMessageType.DISCONNECTED, () => {
            this.isConnecting = false;
            this.needsResync = true;
            this.startDegradedRefresh();
        });
        websocketService.on(WSMessageType.RECONNECTED, () => {
            this.isConnecting = true;
            this.stopDegradedRefresh();
            if (this.needsResync) {
                this.needsResync = false;
                this.emitFileEvent({ type: 'refresh', path: '' });
            }
            this.sendCombinedPathsToBackend();
        });

        window.addEventListener('online', () => {
            if (!this.isConnecting) {
                this.needsResync = true;
                this.startDegradedRefresh();
            }
        });

        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'visible') {
                if (!this.isConnecting) {
                    this.needsResync = true;
                    this.startDegradedRefresh();
                } else {
                    this.emitFileEvent({ type: 'refresh', path: '' });
                }
            }
        });
    }

    private handleFileChange(data: any) {
        if (data) {
            this.isConnecting = true;
            fileEventQueue.addEvent({
                type: data.eventType,
                path: data.name,
                timestamp: Date.now()
            });
        }
    }

    private handleFsWatchEvent(data: any) {
        if (data) {
            this.isConnecting = true;
            fileEventQueue.addEvent({
                type: data.event_type,
                path: data.path,
                timestamp: data.timestamp || Date.now()
            });
        }
    }

    /**
     * Clean up resources and reset state when a user logs out
     */
    public destroy() {
        this.stopDegradedRefresh();
        this.isConnecting = false;

        // Clear debounce timers
        Object.keys(this.writeDebounceTimers).forEach(key => {
            clearTimeout(this.writeDebounceTimers[key]);
        });
        this.writeDebounceTimers = {};

        this.pathListened = [];
        this.jobPathListened = []; // 清空作业路径监听列表

        // 取消文件变化消息处理器
        websocketService.off(WSMessageType.FS_WATCH_EVENT, this.handleFsWatchEvent.bind(this));

        this.emitFileEvent({ type: 'refresh', path: '' });
    }

    private startDegradedRefresh() {
        if (this.degradedRefreshTimer) return;
        this.degradedRefreshTimer = setInterval(() => {
            if (!this.isConnecting) {
                this.emitFileEvent({ type: 'refresh', path: '' });
            }
        }, 5000);
    }

    private stopDegradedRefresh() {
        if (this.degradedRefreshTimer) {
            clearInterval(this.degradedRefreshTimer);
            this.degradedRefreshTimer = null;
        }
    }
    public listenToPath(paths: string[]) {
        this.pathListened = paths
        this.sendCombinedPathsToBackend();
    }

    public listenToJobPath(paths: string[]) {
        this.jobPathListened = paths
        this.sendCombinedPathsToBackend();
    }

    /**
     * 将pathListened和jobPathListened合并并发送给后端
     */
    private sendCombinedPathsToBackend() {
        // 合并两个数组并去除重复项
        const combinedPaths = [...new Set([...this.pathListened, ...this.jobPathListened])];
        if (combinedPaths.length > 0) {
            websocketService.send({
                type: WSMessageType.FS_WATCH_PATHS,
                payload: combinedPaths
            });
        }
    }

    public unListenPath(paths: string[]) {
        this.pathListened = this.pathListened.filter(path => !paths.includes(path));
        this.sendCombinedPathsToBackend();
    }

    public unListenJobPath(paths: string[]) {
        this.jobPathListened = this.jobPathListened.filter(path => !paths.includes(path));
        this.sendCombinedPathsToBackend();
    }

    public listenToJupyterConfig() {
        const jupyterDir = this.baseHome + "/.jupyter"

        if (this.pathListened.includes(jupyterDir)) {
            return
        }
        this.pathListened.push(jupyterDir)
        this.sendCombinedPathsToBackend();
    }

    public unlistenToJupyterConfig() {
        const jupyterDir = this.baseHome + "/.jupyter"
        const index = this.pathListened.indexOf(jupyterDir);
        if (index !== -1) {
            this.pathListened.splice(index, 1);
        }
        this.sendCombinedPathsToBackend();
    }

    public listenToAitomiaConfig() {
        const aitomiaDir = this.baseHome + "/.aitomia"
        if (this.pathListened.includes(aitomiaDir)) {
            return
        }
        this.pathListened.push(aitomiaDir)
        this.listenToPath(this.pathListened)
    }

    public unlistenToAitomiaConfig() {
        const aitomiaDir = this.baseHome + "/.aitomia"
        const index = this.pathListened.indexOf(aitomiaDir);
        if (index !== -1) {
            this.pathListened.splice(index, 1);
        }
        this.listenToPath(this.pathListened)
    }

    // Get the observable for components to subscribe to
    getFileEvents() {
        return this.fileEvents.asObservable();
    }

    // Emit event to all subscribers
    emitFileEvent(event: FileEvent) {
        this.fileEvents.next(event);
    }

    async copyFile(srcPath: string, destPath: string) {
        const res = await fileApi.copyFileResource([{ src: srcPath, dst: destPath, isDir: false }]);
        return res
    }

    async createFile(path: string, name: string, content?: string, force?: boolean) {
        if (!path || !name) {
            getAntdApp().message.error(i18n.t('files_service.path_or_name_empty', { ns: 'common' }))
            return
        }

        if (!validateLegalString(name).isValid) {
            getAntdApp().message.error(getUnsupportedCharsDescription())
            return
        }

        let formData: UploadFile[] = [];

        const data = new Blob([content ?? ""], { type: "text/plain" })
        formData = [{
            uid: '1',
            name: name,
            originFileObj: new File([data], name),
            status: 'done',
            url: ''
        }] as UploadFile[];

        const res = await fileApi.uploadFileResource(formData, path, force);
        return res
    }

    async createFolder(path: string, name: string) {
        if (!path || !name) {
            getAntdApp().message.error(i18n.t('files_service.path_or_name_empty', { ns: 'common' }))
            return
        }

        if (!validateLegalString(name).isValid) {
            getAntdApp().message.error(getUnsupportedCharsDescription())
            return
        }

        const res = await fileApi.uploadFileResource([], pathbe.join(path, name, "/"));
        return res;
    }

    // File operations that emit events after API calls
    async saveFile(path: string, content: string) {
        const res = await fileApi.saveFileResource(path, content);
        db?.deleteFileData(path);
        return res;
    }

    async uploadFiles(files: UploadFile[], path: string, force?: boolean) {

        for (const file of files) {
            if (!validateLegalString(file.name).isValid) {
                getAntdApp().message.error(getUnsupportedCharsDescription())
                return
            }
        }

        if (path.endsWith("/")) {
            const dirName = path.split("/").slice(-2, -1)[0]
            if (!validateLegalString(dirName).isValid) {
                getAntdApp().message.error(getUnsupportedCharsDescription())
                return
            }
        }

        const res = await fileApi.uploadFileResource(files, path, force);
        return res;
    }

    async renameFile(oldPath: string, newFileName: string) {
        if (!validateLegalString(newFileName).isValid) {
            return {
                ok: false,
                message: getUnsupportedCharsDescription(),
                data: undefined
            };
        }
        const res = await fileApi.renameFileResource(oldPath, newFileName);
        return res;
    }

    async moveFile(oldPathList: string[], newPath: string) {
        const res = await fileApi.moveFileResource(oldPathList, newPath);
        return res;
    }

    async deleteFile(pathList: string[]) {
        const res = await fileApi.deleteFileResource(pathList);
        return res;
    }
}

export function isThisAFile(maybeFile: File): Promise<boolean> {
    return new Promise(function (resolve, _reject) {
        if (maybeFile.type !== '') {
            return resolve(true)
        }
        const reader = new FileReader()
        reader.onloadend = () => {
            if (
                reader.error &&
                (
                    reader.error.name === 'NotFoundError' ||
                    reader.error.name === 'NotReadableError'
                )) {
                return resolve(false)
            }
            resolve(true)
        }
        reader.readAsBinaryString(maybeFile)
    })
}

export const isExcelFile = (fileName: string): boolean => {
    return /\.(xlsx|xls|xlsm)$/i.test(fileName);
}

export const isWordFile = (fileName: string): boolean => {
    return /\.(docx|doc)$/i.test(fileName);
}

export const isLogFile = (fileName: string): boolean => {
    return /\.log$/i.test(fileName);
}

export const fileService = new FileService();